// @ts-nocheck
import { useState, useEffect } from 'react';
import { SelectField } from "components/ui/select-field";
import {
    X, Save, AlertCircle, Users, Mail, Bot, Tag, Plus,
    FileText, Settings2, ChevronDown, Sparkles
} from 'lucide-react';

// ─── Field wrapper ────────────────────────────────────────────────────────────
const Field = ({ label, required, error, hint, children }) => (
    <div className="space-y-1.5">
        <label className="flex items-center gap-1 text-[12px] font-bold uppercase tracking-wider text-content-muted dark:text-gray-400">
            {label}
            {required && <span className="text-blue-500 dark:text-blue-400">*</span>}
        </label>
        {children}
        {error && (
            <p className="flex items-center gap-1 text-[11px] font-medium text-red-600 dark:text-red-400">
                <AlertCircle className="w-3 h-3 flex-shrink-0" />
                {error}
            </p>
        )}
        {hint && !error && (
            <p className="text-[11px] font-medium text-content-subtle dark:text-gray-500">{hint}</p>
        )}
    </div>
);

// ─── Styled input ─────────────────────────────────────────────────────────────
const inputCls = (hasError) =>
    `w-full px-3.5 py-2.5 text-[13px] font-medium rounded-xl border transition-all duration-150 outline-none
    bg-surface dark:bg-dark-700 text-content dark:text-white
    placeholder:text-content-subtle dark:placeholder:text-content-muted
    ${hasError
        ? 'border-red-400 dark:border-red-500/60 focus:ring-2 focus:ring-red-500/20'
        : 'border-line dark:border-dark-600 focus:border-blue-500 dark:focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20'
    }`;

// ─── Styled select ────────────────────────────────────────────────────────────
const selectCls = (hasError) =>
    `w-full px-3.5 py-2.5 text-[13px] font-medium rounded-xl border transition-all duration-150 outline-none appearance-none
    bg-surface dark:bg-dark-700 text-content dark:text-white
    ${hasError
        ? 'border-red-400 dark:border-red-500/60 focus:ring-2 focus:ring-red-500/20'
        : 'border-line dark:border-dark-600 focus:border-blue-500 dark:focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20'
    }`;

// ─── Preview chip ─────────────────────────────────────────────────────────────
const PreviewChip = ({ icon: Icon, color, label, sublabel }) => (
    <div className={`mt-2 px-3.5 py-2.5 rounded-xl border flex items-center gap-2.5 ${color}`}>
        <Icon className="w-3.5 h-3.5 flex-shrink-0" />
        <div className="min-w-0">
            <p className="text-meta font-bold truncate">{label}</p>
            {sublabel && <p className="text-micro font-medium opacity-80 truncate">{sublabel}</p>}
        </div>
    </div>
);

// ─── Main component ───────────────────────────────────────────────────────────
const CampaignEditModal = ({
    isOpen,
    onClose,
    onSave,
    campaign,
    segments = [],
    templates = [],
    aiAgents = [],
    existingCampaigns = []
}) => {
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        type: 'promotional',
        segmentId: '',
        templateId: '',
        aiAgentId: '',
        aiGenerated: false,
        abTestingEnabled: false,
        tags: []
    });
    const [errors, setErrors] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [newTag, setNewTag] = useState('');
    const [activeSection, setActiveSection] = useState('basic');

    useEffect(() => {
        if (campaign) {
            setFormData({
                name: campaign.name || '',
                description: campaign.description || '',
                type: campaign.type || 'promotional',
                segmentId: campaign.segmentId || '',
                templateId: campaign.templateId || '',
                aiAgentId: campaign.aiAgentId || '',
                aiGenerated: campaign.aiGenerated || false,
                abTestingEnabled: campaign.abTestingEnabled || false,
                tags: campaign.tags || []
            });
        }
    }, [campaign]);

    useEffect(() => {
        if (!isOpen) {
            setErrors({});
            setNewTag('');
            setActiveSection('basic');
        }
    }, [isOpen]);

    const validateForm = () => {
        const newErrors = {};
        if (!formData.name.trim()) newErrors.name = 'Campaign name is required';
        else if (formData.name.length < 3) newErrors.name = 'Must be at least 3 characters';
        else if (formData.name.length > 100) newErrors.name = 'Must be less than 100 characters';

        const dup = existingCampaigns.find(
            c => c.id !== campaign?.id && c.name.toLowerCase() === formData.name.toLowerCase()
        );
        if (dup) newErrors.name = 'A campaign with this name already exists';

        if (!formData.segmentId) newErrors.segmentId = 'Please select an audience segment';
        if (!formData.templateId) newErrors.templateId = 'Please select a template';
        if (formData.description && formData.description.length > 500)
            newErrors.description = 'Description must be less than 500 characters';

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleInputChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        if (errors[field]) setErrors(prev => ({ ...prev, [field]: undefined }));
    };

    const handleAddTag = () => {
        if (newTag.trim() && !formData.tags.includes(newTag.trim())) {
            setFormData(prev => ({ ...prev, tags: [...prev.tags, newTag.trim()] }));
            setNewTag('');
        }
    };

    const handleRemoveTag = (tag) => {
        setFormData(prev => ({ ...prev, tags: prev.tags.filter(t => t !== tag) }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;
        setIsLoading(true);
        try {
            await onSave({ ...campaign, ...formData, updatedAt: new Date().toISOString() });
            onClose();
        } catch {
            setErrors({ submit: 'Failed to update campaign. Please try again.' });
        } finally {
            setIsLoading(false);
        }
    };

    const selectedSegment = segments.find(s => s.id === formData.segmentId);
    const selectedTemplate = templates.find(t => t.id === formData.templateId);
    const selectedAIAgent = aiAgents.find(a => a.id === formData.aiAgentId);

    if (!isOpen) return null;

    const sections = [
        { id: 'basic', label: 'Basic Info', icon: FileText },
        { id: 'audience', label: 'Audience', icon: Users },
        { id: 'settings', label: 'Settings', icon: Settings2 },
    ];

    const TYPES = [
        { value: 'promotional', label: 'Promotional' },
        { value: 'automated', label: 'Automated' },
        { value: 'announcement', label: 'Announcement' },
        { value: 'sequence', label: 'Sequence' },
        { value: 'welcome', label: 'Welcome' },
        { value: 'newsletter', label: 'Newsletter' },
    ];

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            {/* Backdrop */}
            <div
                className="absolute inset-0 bg-black/60 backdrop-blur-sm"
                onClick={onClose}
            />

            {/* Modal */}
            <div className="relative w-full max-w-2xl max-h-[92vh] flex flex-col bg-surface dark:bg-dark-800 rounded-2xl shadow-2xl border border-line dark:border-dark-700 overflow-hidden">

                {/* Top gradient accent */}
                <div className="absolute top-0 inset-x-0 h-[3px] bg-gradient-to-r from-blue-500 via-indigo-500 to-violet-500" />

                {/* ── Header ── */}
                <div className="flex items-center justify-between px-6 pt-6 pb-4 flex-shrink-0">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-sm flex-shrink-0">
                            <Mail className="w-5 h-5 text-white" />
                        </div>
                        <div>
                            <h2 className="text-[16px] font-bold text-content dark:text-white">Edit Campaign</h2>
                            <p className="text-[12px] font-medium text-content-muted dark:text-gray-400">
                                {campaign?.name ? `Editing "${campaign.name}"` : 'Update campaign details and settings'}
                            </p>
                        </div>
                    </div>
                    <button aria-label="Close"
                        onClick={onClose}
                        className="w-8 h-8 rounded-xl flex items-center justify-center text-content-subtle dark:text-gray-500 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-dark-700 transition-colors"
                    >
                        <X className="w-4 h-4" />
                    </button>
                </div>

                {/* ── Tab nav ── */}
                <div className="flex items-center gap-1 px-6 pb-1 flex-shrink-0 border-b border-line dark:border-dark-700">
                    {sections.map(({ id, label, icon: Icon }) => (
                        <button
                            key={id}
                            type="button"
                            onClick={() => setActiveSection(id)}
                            className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-[12px] font-semibold transition-all duration-150 mb-[-1px] border-b-2 ${
                                activeSection === id
                                    ? 'text-blue-600 dark:text-blue-400 border-blue-500 dark:border-blue-400'
                                    : 'text-content-muted dark:text-gray-400 border-transparent hover:text-gray-700 dark:hover:text-gray-200'
                            }`}
                        >
                            <Icon className="w-3.5 h-3.5" />
                            {label}
                        </button>
                    ))}
                </div>

                {/* ── Body ── */}
                <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
                    <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">

                        {/* ── BASIC INFO ── */}
                        {activeSection === 'basic' && (
                            <>
                                <Field label="Campaign Name" required error={errors.name}>
                                    <input
                                        type="text"
                                        value={formData.name}
                                        onChange={e => handleInputChange('name', e.target.value)}
                                        className={inputCls(!!errors.name)}
                                        placeholder="e.g. Summer Promo 2026"
                                        maxLength={100}
                                    />
                                    <div className="flex justify-end mt-1">
                                        <span className="text-[10px] font-medium text-content-subtle dark:text-gray-500">{formData.name.length}/100</span>
                                    </div>
                                </Field>

                                <Field label="Description" error={errors.description} hint="Optional — max 500 characters">
                                    <textarea
                                        value={formData.description}
                                        onChange={e => handleInputChange('description', e.target.value)}
                                        className={`${inputCls(!!errors.description)} resize-none`}
                                        placeholder="Describe the purpose of this campaign…"
                                        rows={3}
                                        maxLength={500}
                                    />
                                    <div className="flex justify-end -mt-0.5">
                                        <span className="text-[10px] font-medium text-content-subtle dark:text-gray-500">{formData.description.length}/500</span>
                                    </div>
                                </Field>

                                <Field label="Campaign Type" required>
                                    <div className="relative">
                                        <SelectField
                                            value={formData.type}
                                            onChange={e => handleInputChange('type', e.target.value)}
                                            className={selectCls(false)}
                                        >
                                            {TYPES.map(t => (
                                                <option key={t.value} value={t.value}>{t.label}</option>
                                            ))}
                                        </SelectField>
                                        <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-content-subtle pointer-events-none" />
                                    </div>
                                </Field>

                                <Field label="Tags">
                                    <div className="flex gap-2">
                                        <input
                                            type="text"
                                            value={newTag}
                                            onChange={e => setNewTag(e.target.value)}
                                            onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), handleAddTag())}
                                            className={inputCls(false)}
                                            placeholder="Type a tag and press Enter…"
                                            maxLength={20}
                                        />
                                        <button aria-label="Add"
                                            type="button"
                                            onClick={handleAddTag}
                                            className="flex-shrink-0 h-[42px] w-[42px] rounded-xl bg-gray-100 dark:bg-dark-700 border border-line dark:border-dark-600 flex items-center justify-center text-content-muted dark:text-gray-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 hover:text-blue-600 dark:hover:text-blue-400 hover:border-blue-200 dark:hover:border-blue-700/50 transition-all"
                                        >
                                            <Plus className="w-4 h-4" />
                                        </button>
                                    </div>
                                    {formData.tags.length > 0 && (
                                        <div className="flex flex-wrap gap-2 mt-2">
                                            {formData.tags.map((tag, i) => (
                                                <span
                                                    key={i}
                                                    className="inline-flex items-center gap-1 px-2.5 py-1 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 border border-blue-100 dark:border-blue-800/40 rounded-lg text-[11px] font-semibold"
                                                >
                                                    <Tag className="w-2.5 h-2.5" />
                                                    {tag}
                                                    <button
                                                        type="button"
                                                        onClick={() => handleRemoveTag(tag)}
                                                        className="ml-0.5 hover:bg-blue-200 dark:hover:bg-blue-800/40 rounded p-0.5 transition-colors"
                                                    >
                                                        <X className="w-2.5 h-2.5" />
                                                    </button>
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                </Field>
                            </>
                        )}

                        {/* ── AUDIENCE ── */}
                        {activeSection === 'audience' && (
                            <>
                                <Field label="Audience Segment" required error={errors.segmentId}>
                                    <div className="relative">
                                        <SelectField
                                            value={formData.segmentId}
                                            onChange={e => handleInputChange('segmentId', e.target.value)}
                                            className={selectCls(!!errors.segmentId)}
                                        >
                                            <option value="">Select an audience segment</option>
                                            {segments.map(s => (
                                                <option key={s.id} value={s.id}>
                                                    {s.name} ({s.size?.toLocaleString() || 0} contacts)
                                                </option>
                                            ))}
                                        </SelectField>
                                        <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-content-subtle pointer-events-none" />
                                    </div>
                                    {selectedSegment && (
                                        <PreviewChip
                                            icon={Users}
                                            color="bg-blue-50 dark:bg-blue-900/15 border border-blue-100 dark:border-blue-800/40 text-blue-700 dark:text-blue-300"
                                            label={selectedSegment.name}
                                            sublabel={`${selectedSegment.size?.toLocaleString() || 0} contacts`}
                                        />
                                    )}
                                </Field>

                                <Field label="Email Template" required error={errors.templateId}>
                                    <div className="relative">
                                        <SelectField
                                            value={formData.templateId}
                                            onChange={e => handleInputChange('templateId', e.target.value)}
                                            className={selectCls(!!errors.templateId)}
                                        >
                                            <option value="">Select a template</option>
                                            {templates.map(t => (
                                                <option key={t.id} value={t.id}>{t.name}</option>
                                            ))}
                                        </SelectField>
                                        <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-content-subtle pointer-events-none" />
                                    </div>
                                    {selectedTemplate && (
                                        <PreviewChip
                                            icon={Mail}
                                            color="bg-gray-50 dark:bg-dark-700 border border-line-subtle dark:border-dark-600 text-gray-700 dark:text-gray-300"
                                            label={selectedTemplate.name}
                                            sublabel={selectedTemplate.description}
                                        />
                                    )}
                                </Field>

                                <Field label="AI Agent" hint="Optional — assign an AI agent to optimise this campaign">
                                    <div className="relative">
                                        <SelectField
                                            value={formData.aiAgentId}
                                            onChange={e => {
                                                const id = e.target.value;
                                                handleInputChange('aiAgentId', id);
                                                handleInputChange('aiGenerated', !!id);
                                            }}
                                            className={selectCls(false)}
                                        >
                                            <option value="">No AI agent</option>
                                            {aiAgents.map(a => (
                                                <option key={a.id} value={a.id}>
                                                    {a.name} — {a.specialty}
                                                </option>
                                            ))}
                                        </SelectField>
                                        <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-content-subtle pointer-events-none" />
                                    </div>
                                    {selectedAIAgent && (
                                        <PreviewChip
                                            icon={Bot}
                                            color="bg-violet-50 dark:bg-violet-900/15 border border-violet-100 dark:border-violet-800/40 text-violet-700 dark:text-violet-300"
                                            label={selectedAIAgent.name}
                                            sublabel={`${selectedAIAgent.specialty} · Score ${selectedAIAgent.performance?.score || 0}/100`}
                                        />
                                    )}
                                </Field>
                            </>
                        )}

                        {/* ── SETTINGS ── */}
                        {activeSection === 'settings' && (
                            <div className="space-y-3">
                                <p className="text-[12px] font-bold uppercase tracking-wider text-content-subtle dark:text-gray-500">Campaign Options</p>

                                {/* A/B Toggle */}
                                <label className="flex items-start gap-3 p-4 rounded-xl border border-line dark:border-dark-700 bg-gray-50/50 dark:bg-dark-700/30 cursor-pointer hover:border-blue-200 dark:hover:border-blue-700/50 hover:bg-blue-50/30 dark:hover:bg-blue-900/10 transition-all group">
                                    <div className="flex-shrink-0 mt-0.5">
                                        <input
                                            type="checkbox"
                                            checked={formData.abTestingEnabled}
                                            onChange={e => handleInputChange('abTestingEnabled', e.target.checked)}
                                            className="w-4 h-4 rounded border-line-strong dark:border-dark-500 text-blue-600 dark:text-blue-300 focus:ring-2 focus:ring-blue-500/20 accent-blue-600"
                                        />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2">
                                            <Sparkles className="w-3.5 h-3.5 text-blue-500 dark:text-blue-400 flex-shrink-0" />
                                            <span className="text-[13px] font-bold text-content dark:text-white">Enable A/B Testing</span>
                                        </div>
                                        <p className="text-[11px] font-medium text-content-muted dark:text-gray-400 mt-0.5">
                                            Automatically optimise this campaign through A/B testing variants.
                                        </p>
                                    </div>
                                    {formData.abTestingEnabled && (
                                        <span className="flex-shrink-0 px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wide bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400">
                                            On
                                        </span>
                                    )}
                                </label>
                            </div>
                        )}

                        {/* Submit error */}
                        {errors.submit && (
                            <div className="relative overflow-hidden rounded-xl border border-red-100 dark:border-red-900/30 bg-red-50/60 dark:bg-red-900/10 px-4 py-3">
                                <div className="absolute top-0 inset-x-0 h-[2px] bg-gradient-to-r from-red-400 to-rose-500" />
                                <p className="flex items-center gap-2 text-[12px] font-semibold text-red-700 dark:text-red-400">
                                    <AlertCircle className="w-4 h-4 flex-shrink-0" />
                                    {errors.submit}
                                </p>
                            </div>
                        )}
                    </div>

                    {/* ── Footer ── */}
                    <div className="flex items-center justify-between gap-3 px-6 py-4 border-t border-line dark:border-dark-700 bg-gray-50/50 dark:bg-dark-800/60 flex-shrink-0">
                        {/* Section navigation */}
                        <div className="flex items-center gap-1.5">
                            {sections.map(({ id }) => (
                                <div
                                    key={id}
                                    className={`w-1.5 h-1.5 rounded-full transition-all duration-150 ${
                                        activeSection === id
                                            ? 'bg-blue-500 w-4'
                                            : 'bg-gray-300 dark:bg-dark-600'
                                    }`}
                                />
                            ))}
                        </div>

                        <div className="flex items-center gap-2.5">
                            <button
                                type="button"
                                onClick={onClose}
                                disabled={isLoading}
                                className="h-9 px-4 rounded-xl text-[13px] font-semibold text-content-secondary dark:text-gray-300 bg-surface dark:bg-dark-700 border border-line dark:border-dark-600 hover:bg-gray-50 dark:hover:bg-dark-600 hover:border-line-strong dark:hover:border-dark-500 transition-all shadow-sm disabled:opacity-50"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                disabled={isLoading}
                                className="h-9 px-5 rounded-xl text-[13px] font-bold text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 shadow-sm hover:shadow-md transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                            >
                                {isLoading ? (
                                    <div className="w-3.5 h-3.5 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                                ) : (
                                    <Save className="w-3.5 h-3.5" />
                                )}
                                {isLoading ? 'Saving…' : 'Save Changes'}
                            </button>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default CampaignEditModal;
